import React, {
    useState,
    useEffect,
    useCallback
} from 'react';


import {
    useCurationConfigState,
    useSetCurationConfigState
} from '../../../hooks/widgetConfiguration.js';

import useListeningSearchTracking from '../hooks/useListeningSearchTracking.js';

import useGetBrowserLanguage from '../hooks/useGetBrowserLanguage.js';

import {
    RIQInfoPopover
} from '../../../components/RIQPopover.jsx';

import {
    useVerifySearch,
    ValidationStates
} from '../hooks/useVerifySearch.js';

import {
    useSetShowInstantSearchModal
} from './InstantSearchInfoModal.jsx';

import RIQButton from '../../../components/RIQButton.jsx';
import RIQPillInput from '../../../components/RIQPillInput.jsx';

import {
    SearchRecommendations
} from './SearchRecommendations.jsx';
import PoweredByQuid from './PoweredByQuid.jsx';
import QueryValidationError from './QueryValidationError.jsx';

import {
    listeningDefinitions
} from './enums/listeningDefinitions.jsx';

const {
    primaryTermsPlaceholder,
    sourceInfoBubble
} = listeningDefinitions['instant-search'];

export default function NoSearchTerm() {

    const markModalEligibleToShow = useSetShowInstantSearchModal()[1];

    const [searchTerms, setSearchTerms] = useCurationConfigState('searchTerms');

    const [currentSearchTerms, setCurrentSearchTerms] = useState(searchTerms || []);

    const [language, setLanguage] = useCurationConfigState('language');

    const defaultLanguage = useGetBrowserLanguage();

    const listeningSearchTracking = useListeningSearchTracking();

    const [updatingSearchTerms, setUpdatingSearchTerms] = useState(false);

    const {validationState, queryValidationErrors} = useVerifySearch(updatingSearchTerms && !_.isEmpty(currentSearchTerms), {
        searchTerms: currentSearchTerms,
        language: language || defaultLanguage
    }, true);

    const isQueryValid = !_.find(queryValidationErrors?.searchTerms, {
        isFatal: true
    });

    const searchTermsWarning = _.findLast(queryValidationErrors?.searchTerms, {
        isFatal: false
    });

    const setShowAdvancedSearchEditor = useSetCurationConfigState('showAdvancedSearchEditor');

    let searchTermsError;
    if (queryValidationErrors && !isQueryValid && !_.isEmpty(currentSearchTerms)) {
        searchTermsError = _.find(queryValidationErrors.searchTerms, {
            isFatal: true
        });
    }

    let specificPillError = _.last(queryValidationErrors?.searchTerms, {
        isFatal: true
    });
    if (!_.isFinite(specificPillError?.idx)) {
        specificPillError = null;
    }

    useEffect(() => {
        if (updatingSearchTerms) {
            if (validationState === ValidationStates.Valid) {
                setSearchTerms(currentSearchTerms);
                if (!language) {
                    setLanguage(defaultLanguage);
                }

                listeningSearchTracking({
                    searchTerms: currentSearchTerms,
                    language: language || defaultLanguage
                });
            }
        }
    }, [currentSearchTerms, updatingSearchTerms, validationState, setSearchTerms, language, listeningSearchTracking, setLanguage, defaultLanguage]);


    function onUpdateSearchTermPills(newSearchTerms) {
        markModalEligibleToShow(true);

        setUpdatingSearchTerms(false);
        setCurrentSearchTerms(newSearchTerms);
    }

    function onSearch() {
        setUpdatingSearchTerms(true);
    }

    const showEditor = useCallback(function showEditor() {
        setSearchTerms(currentSearchTerms);
        setShowAdvancedSearchEditor(true);

        RivalIQ.execute('tracking:push:auth', RivalIQ.tracking.metric.ListeningClickedEdit, {
            source: `no-search-term`
        });
    }, [setSearchTerms, currentSearchTerms, setShowAdvancedSearchEditor]);

    return (
        <>
            <div className='tlw-relative tlw-ml-auto tlw-mr-auto tlw-mt-25 tlw-flex tlw-max-w-[67rem] tlw-flex-col tlw-items-center tlw-justify-center'>
                <h3 className='tlw-m-0 tlw-mb-7.5 tlw-font-bold'>Analyze consumer perceptions with Instant Search</h3>
                <div className='tlw-mb-20 tlw-text-gray-700'>
                    Search millions of sites like Reddit, Twitter, blogs, news, and more.
                    <RIQInfoPopover
                        description={sourceInfoBubble}
                    />
                </div>
                <div className='tlw-flex tlw-w-full tlw-items-stretch'>
                    <RIQPillInput
                        inputUniqueId='primaryTerms'
                        placeHolder={primaryTermsPlaceholder}
                        className='tlw-h-27 tlw-grow tlw-border-r-0'
                        pills={currentSearchTerms}
                        setPills={onUpdateSearchTermPills}
                        focusedPillIdx={specificPillError?.idx}
                    />
                    <div>
                        <RIQButton
                            variant='search'
                            className='tlw-h-27 tlw-w-60 tlw-shrink-0'
                            onClick={onSearch}
                            disabled={updatingSearchTerms || !isQueryValid}
                        >
                            <span className='fa fa-search'/>
                        &nbsp; Search
                        </RIQButton>
                        <div className='tlw-mt-4 tlw-text-center tlw-text-11'>
                            <RIQButton
                                variant='link'
                                onClick={showEditor}
                            >
                                Advanced search
                            </RIQButton>
                        </div>
                    </div>
                </div>

                <div
                    className='tlw-h-[75px] tlw-w-full'
                >
                    <QueryValidationError
                        searchTermsError={searchTermsError}
                        searchTermsWarning={searchTermsWarning}
                    />

                    {validationState === ValidationStates.Validating && updatingSearchTerms &&
                        <div className='tlw-grid tlw-h-full tlw-place-items-center'>
                            <div className='tlw-text-[36px] tlw-text-gray-500'>
                                <span className='fa fa-circle-o-notch fa-spin'></span>
                            </div>
                        </div>
                    }
                </div>

                <SearchRecommendations
                    instanceId='recommendations'
                />

                <PoweredByQuid />
            </div>
        </>
    );
}
